import { Group, Paper, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import AdminMerchantInfo from '../../components/admin-merchant-info/admin-merchant-info.component';
import AdminTable from '../../components/admin-table/admin-table.component';
import MerchantFilter from '../../components/admin-table/components/merchant-filter.component';
import UpdateDisableButton from '../../components/admin-table/components/update-disable.button';
import AdminUpdateMerchant from '../../components/admin-update-merchant/admin-update-merchant.component';
import SearchButton from '../../components/buttons/search-button/search-button.component';
import XPagination from '../../components/x-components/x-pagination.component';
import { BusinessTypesCode } from '../../enums/business-type';
import { MerchantOnboardingStatus } from '../../enums/merchant-onboarding-status';
import { OpenComponent } from '../../enums/misc.enum';
import { Roles } from '../../enums/roles-enum';
import useAuth from '../../hooks/useAuth';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import {
  BasicMerchant,
  IPaginationMeta,
  RetrieveMerchantsRequest,
} from '../../type/admin.types';
import { ErrorResponse } from '../../type/error-response.types';
import { MerchantFilters } from '../../type/filter.types';

function AdminOnboardingPage() {
  const axiosPrivate = useAxiosPrivate();
  const [merchants, setMerchants] = useState<BasicMerchant[]>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [refreshTable, setRefreshTable] = useState(false);
  const [search, setSearch] = useState<string>('');
  const { auth } = useAuth();
  const isViewOnly =
    auth?.role === Roles.SETTLEMENT || auth?.role === Roles.CHECKER;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checkedAll, setCheckedAll] = useState(false);
  const [componentManager, setComponentManager] = useState<OpenComponent>(
    OpenComponent.HOME
  );
  const [selectedUser, setSelectedUser] = useState<number>();
  const [page, setPage] = useState(1);

  const [filter, setFilter] = useState<MerchantFilters>({
    onboardingStatus: 'ALL',
    isActive: 'ALL',
    businessType: 'ALL',
    username: '',
  });

  const fetchMerchants = async (page: number, clearIsChecked: boolean) => {
    setIsLoading(true);
    const currentPage = page;
    let paramFilters: TemporaryFilters = {
      limit: 20,
    };

    const endpoint = '/web/onboard';
    try {
      if (
        filter.onboardingStatus === MerchantOnboardingStatus.APPROVED ||
        filter.onboardingStatus === MerchantOnboardingStatus.DECLINED ||
        filter.onboardingStatus === MerchantOnboardingStatus.PENDING ||
        filter.onboardingStatus === MerchantOnboardingStatus.NEW
      ) {
        paramFilters = {
          onboardingStatus: filter.onboardingStatus,
        };
      }

      if (filter.isActive === 'ENABLED') {
        paramFilters = {
          ...paramFilters,
          isActive: true,
        };
      } else if (filter.isActive === 'DISABLED') {
        paramFilters = {
          ...paramFilters,
          isActive: false,
        };
        delete paramFilters.onboardingStatus;
      }

      if (filter.businessType !== 'ALL') {
        paramFilters = {
          ...paramFilters,
          businessType: filter.businessType as BusinessTypesCode,
        };
      }

      const request: RetrieveMerchantsRequest = {
        page: currentPage,
        ...paramFilters,
        limit: 20,
      };

      if (filter.username?.trim()) {
        request.merchantName = filter.username;
      }

      const response = await axiosPrivate.get(endpoint, {
        params: request,
      });

      setMerchants(response.data.items);
      setPaginationData(response.data.metadata);
      if (clearIsChecked) {
        setIsChecked([]);
        setCheckedAll(false);
      }
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-merchants',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants(1, true);

    //When filters change, refresh the table
  }, [filter]);

  useDidMountEffect(() => {
    fetchMerchants(page, true);
    //update page
  }, [page]);

  useEffect(() => {
    setFilter((prev) => {
      return {
        ...prev,
        username: search,
      };
    });
  }, [search]);

  useDidMountEffect(() => {
    paginationData?.currentPage &&
      fetchMerchants(paginationData?.currentPage, false);
  }, [refreshTable]);

  return (
    <>
      {componentManager === OpenComponent.HOME ? (
        <>
          {/* <div className='admin-table'></div> */}
          <Title order={2} my='lg'>
            View All Onboarding Records
          </Title>

          <Paper
            withBorder
            radius='md'
            shadow='lg'
            style={{
              flex: 1,
              height: '100%',
              padding: '20px 0',
              overflow: 'auto',
            }}
          >
            <Group p='md' grow>
              <Group position='apart'>
                <SearchButton
                  setSearch={setSearch}
                  disabled={isLoading}
                  className='alternative-mantine-disable'
                  search={search}
                />
                {!isViewOnly && (
                  <UpdateDisableButton
                    isChecked={isChecked}
                    setRefreshTable={setRefreshTable}
                    merchants={merchants}
                    setComponentManager={setComponentManager}
                  />
                )}
              </Group>
            </Group>
            <MerchantFilter
              setFilter={setFilter}
              isLoading={isLoading}
              filter={filter}
            />
            <AdminTable
              type='all'
              merchants={merchants}
              isLoading={isLoading}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              checkedAll={checkedAll}
              setCheckedAll={setCheckedAll}
              setRefreshTable={setRefreshTable}
              setComponentManager={setComponentManager}
              setSelectedUser={setSelectedUser}
            />
            {paginationData && paginationData.totalPages ? (
              <XPagination
                page={paginationData.currentPage}
                total={paginationData.totalPages}
                setPage={setPage}
              />
            ) : (
              <></>
            )}
          </Paper>
        </>
      ) : componentManager === OpenComponent.VIEW_USER && selectedUser ? (
        <AdminMerchantInfo
          merchantId={selectedUser}
          setComponentManager={setComponentManager}
          setSelectedUser={setSelectedUser}
          setRefreshTable={setRefreshTable}
          refreshTable={refreshTable}
        />
      ) : componentManager === OpenComponent.UPDATE_USER ? (
        <AdminUpdateMerchant
          merchantId={isChecked[0]}
          setRefreshTable={setRefreshTable}
          setComponentManager={setComponentManager}
        />
      ) : (
        <>Error in component switching</>
      )}
    </>
  );
}

export default AdminOnboardingPage;

interface TemporaryFilters {
  limit?: number;
  merchantName?: string;
  onboardingStatus?: MerchantOnboardingStatus;
  businessType?: BusinessTypesCode;
  isActive?: boolean;
}
